import React from 'react';
import type { Goal } from '../types';
// Card system styles
import '../styles/card-system.css';
// 🎨 JAN 19, 2026: Import WorkoutIcon for goal icon display
import { WorkoutIcon } from '../utils/workoutIconMapper';

interface ActiveGoalsSectionProps {
  data: {
    goals: Goal[];
  } | null;
  onNavigateToGoals: () => void;
  onGoalClick?: (goalId: string) => void; // 🆕 JAN 21, 2026: Direct goal navigation
}

export const ActiveGoalsSection: React.FC<ActiveGoalsSectionProps> = ({ data, onNavigateToGoals, onGoalClick }) => {
  if (!data) return null;

  // Get active goals (using same filter as "Current" in browse view)
  const activeGoals = data.goals.filter(goal => goal.status === 'active');

  // Don't render if no active goals
  if (activeGoals.length === 0) return null;

  // Calculate progress for a goal (matching the logic from GoalCardWrapper)
  const getProgressPercentage = (goal: Goal) => {
    if (goal.journey?.phases) {
      const totalPhases = goal.journey.phases.length;
      const completedPhases = goal.journey.phases.filter(p => p.status === 'completed').length;
      const currentPhaseProgress = goal.journey.currentPhase / totalPhases;
      return Math.round((completedPhases + currentPhaseProgress) / totalPhases * 100);
    }
    // Fallback to weeks completed
    if (goal.progress?.weeklyPlansCompleted && goal.progress?.totalWeeksPlanned) {
      return Math.round((goal.progress.weeklyPlansCompleted / goal.progress.totalWeeksPlanned) * 100);
    }
    return 0;
  };

  // Get progress bar color class based on percentage
  const getProgressColorClass = (percentage: number) => {
    if (percentage >= 80) return 'mypath-progress-fill success';
    if (percentage >= 50) return 'mypath-progress-fill primary';
    if (percentage >= 25) return 'mypath-progress-fill accent';
    return 'mypath-progress-fill warning';
  };

  // 🆕 JAN 21, 2026: Handle individual goal tap - navigate to goal detail
  const handleGoalTap = (goalId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card tap
    if (onGoalClick) {
      onGoalClick(goalId);
    }
  };

  // Handle card tap - navigate to goals browse view (fallback if no goal clicked)
  const handleCardTap = () => {
    onNavigateToGoals();
  };

  // Show all active goals (no limit)
  const displayGoals = activeGoals;

  return (
    <>
      {/* 🎨 JAN 19, 2026: Match title styling with "Today's Focus" and "This Week" */}
      <h2 className="text-xl font-semibold text-gray-900 mb-3 px-4">Active Goals</h2>
      
      {/* 🎨 JAN 23, 2026: Dark green wrapper (matches This Week structure) with inner white card */}
      <div className="px-4">
        <div className="rounded-lg p-4" style={{
          background: '#1F6C4F', // Dark cedar green wrapper
          border: 'none'
        }}>
          <div 
            className="mypath-card cursor-pointer"
            style={{ 
              backgroundColor: 'white',
              padding: '12px',
              border: 'none'
            }}
            onClick={handleCardTap}
          >
            <div className="space-y-3">
              {displayGoals.map((goal, index) => {
                  const progressPercentage = getProgressPercentage(goal);
                  
                  return (
                    <div 
                      key={goal.id} 
                      className={`flex items-center justify-between ${index > 0 ? 'pt-2 border-t' : ''} cursor-pointer rounded-lg px-2 py-1 -mx-2 transition-colors`} 
                      style={{ 
                        borderColor: 'var(--cedar-limestone)'
                      }}
                      onClick={(e) => handleGoalTap(goal.id, e)}
                    >
                      {/* 🎨 JAN 19, 2026: Icon at left (consistent with GoalCard) */}
                      {goal.iconType && (
                        <div className="flex-shrink-0 mr-3">
                          <WorkoutIcon 
                            type={goal.iconType} 
                            size={28} 
                          />
                        </div>
                      )}
                      
                      {/* Goal title */}
                      <div className="flex-1 pr-3">
                        <h3 className="mypath-card-title" style={{ fontSize: '14px', color: '#000' }}>
                          {goal.title}
                        </h3>
                      </div>
                      
                      {/* Progress indicator */}
                      <div className="w-12 flex-shrink-0 flex items-center">
                        <div className="mypath-progress-bar" style={{ 
                          height: '6px', 
                          backgroundColor: 'var(--colorsurface-50)', 
                          border: '1px solid #9CA3AF'
                        }}>
                          <div 
                            className={getProgressColorClass(progressPercentage)}
                            style={{ width: `${Math.max(progressPercentage, 8)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
